import React, { useCallback } from 'react';
import { Alert } from '@mui/material';
import { SpreadsheetDocumentsData } from '../../models/SpreadsheetDocument';
import {
  AccountSpreadsheetService,
  ACCOUNT_UPDATED_COLUMN,
  DEFAULT_ACCOUNT_COLUMNS,
} from '../../services/AccountSpreadsheetService';
import SpreadsheetDocuments from '../../components/spreadsheet-docs/SpreadsheetDocuments';
import { getUserInfo } from '../../utils/auth';

interface AccountSpreadsheetProps {
  projectId: number;
}

/**
 * 현장 계정 정보 — 다중 문서 스프레드시트.
 *
 * IP/네트워크·자재와 같은 그리드 UI 를 쓴다(셀 직접 입력 / 엑셀 업로드·내보내기).
 * 저장은 projects.account_spreadsheet JSON 컬럼.
 *
 * 폐쇄망 사내 사용이라 값은 평문으로 저장한다. 다만 viewer 까지 볼 필요는 없어
 * 백엔드가 조회도 admin/editor 로 막아 두었다 — viewer 는 403 이므로 그리드를
 * 그리지 않고 안내만 보여준다.
 */
const AccountSpreadsheet: React.FC<AccountSpreadsheetProps> = ({ projectId }) => {
  const role = getUserInfo()?.role;
  const allowed = role === 'admin' || role === 'editor';

  const load = useCallback(() => AccountSpreadsheetService.get(projectId), [projectId]);
  const save = useCallback(
    (data: SpreadsheetDocumentsData) => AccountSpreadsheetService.update(projectId, data),
    [projectId],
  );

  if (!allowed) {
    return (
      <Alert severity="info" variant="outlined">
        계정 정보는 admin / editor 권한에서만 조회할 수 있습니다.
      </Alert>
    );
  }

  return (
    <SpreadsheetDocuments
      load={load}
      save={save}
      writable
      title="계정 정보"
      description="💡 장비·서비스 계정을 표로 관리합니다. 셀을 클릭해 직접 입력(Enter 로 다음 행), 컬럼 헤더 더블 클릭으로 이름 변경, 헤더 오른쪽 경계를 끌어 폭 조절. 엑셀 업로드로 문서를 추가할 수 있고 모든 변경은 자동 저장됩니다. 행의 값을 고치면 '업데이트 날짜' 가 오늘 날짜로 자동 기입됩니다(직접 입력하면 그 값이 유지)."
      defaultColumns={DEFAULT_ACCOUNT_COLUMNS}
      stampColumnName={ACCOUNT_UPDATED_COLUMN}
      newDocName="계정 문서"
      exportPrefix="accounts"
    />
  );
};

export default AccountSpreadsheet;
